import { MigrationType } from "../../models/Statistics/MigrationType";
import { FlaggedPathOccurrence } from "../../models/FlaggedPath";

export const getMigrationEntityIdLabel = (migrationType: MigrationType): string => {
  const labelMapping: Record<MigrationType, string> = {
    [MigrationType.CasesMigration]: "Case Number",
    [MigrationType.CaseFoldersMigration]: "Case Folder Path",
    [MigrationType.IntakesMigration]: "Intake Number",
    [MigrationType.ProvidersMigration]: "Provider Name ID",
    [MigrationType.FirmDocumentsMigration]: "Firm Documents Folder Path",
    [MigrationType.TemplatesMigration]: "Template Type",
    [MigrationType.ProviderFoldersMigration]: "Provider Folder Path",
  };
  return labelMapping[migrationType] || "Entity ID";
};

/** The occurrences one entity owns, and the identity they share. */
export interface EntityOccurrenceGroup {
  key: string;
  migrationType: MigrationType;
  migrationEntityId: string;
  entityId?: string;
  occurrences: FlaggedPathOccurrence[];
}

/**
 * Groups a path's occurrences by the entity that owns them.
 *
 * One case can reach a folder through several source records, and listing those flat reads as several
 * unrelated hits on the same case. Grouping is also what the allow-discovery decision is made against: it is
 * about the entity and the folder, not about the tab the link happened to sit on.
 */
export const groupOccurrencesByEntity = (
  occurrences: FlaggedPathOccurrence[]
): EntityOccurrenceGroup[] => {
  const groups = new Map<string, EntityOccurrenceGroup>();

  for (const occurrence of occurrences) {
    const key = `${occurrence.migrationType}::${occurrence.migrationEntityId.toLowerCase()}`;
    const existing = groups.get(key);

    if (existing) {
      existing.occurrences.push(occurrence);
      // An entity key can outlive the row it pointed at, so the first non-empty id wins rather than the first.
      existing.entityId ??= occurrence.entityId;
    } else {
      groups.set(key, {
        key,
        migrationType: occurrence.migrationType,
        migrationEntityId: occurrence.migrationEntityId,
        entityId: occurrence.entityId,
        occurrences: [occurrence],
      });
    }
  }

  const enumOrder: MigrationType[] = [
    MigrationType.CasesMigration,
    MigrationType.CaseFoldersMigration,
    MigrationType.ProvidersMigration,
    MigrationType.FirmDocumentsMigration,
    MigrationType.TemplatesMigration,
    MigrationType.IntakesMigration,
    MigrationType.ProviderFoldersMigration,
  ];

  return Array.from(groups.values()).sort((a, b) => {
    const byType = enumOrder.indexOf(a.migrationType) - enumOrder.indexOf(b.migrationType);
    if (byType !== 0) return byType;
    return a.migrationEntityId.localeCompare(b.migrationEntityId, undefined, { numeric: true });
  });
};

/** How the source names the record, the way the source-links page renders one: Correspondence.CorrespondID = 48210. */
export const describeSourceRecord = (occurrence: FlaggedPathOccurrence): string | null => {
  if (!occurrence.sourceTable && !occurrence.sourceRecordId) return null;

  const record = occurrence.sourceRecordColumn && occurrence.sourceRecordId
    ? `${occurrence.sourceRecordColumn} = ${occurrence.sourceRecordId}`
    : occurrence.sourceRecordId;

  return occurrence.sourceTable && record
    ? `${occurrence.sourceTable}.${record}`
    : occurrence.sourceTable ?? record ?? null;
};

export const getEntityUrl = (organizationId: string, migrationType: MigrationType, entityId: string): string => {
  switch (migrationType) {
    case MigrationType.CasesMigration:
    case MigrationType.CaseFoldersMigration:
      return `/organization/${organizationId}/case/${entityId}`;
    case MigrationType.IntakesMigration:
      return `/organization/${organizationId}/intake/${entityId}`;
    case MigrationType.ProvidersMigration:
      return `/organization/${organizationId}/provider/${entityId}`;
    case MigrationType.FirmDocumentsMigration:
      return `/organization/${organizationId}/firm-documents-folder/${entityId}`;
    case MigrationType.TemplatesMigration:
      return `/organization/${organizationId}/template/${entityId}`;
    default:
      return `/organization/${organizationId}`;
  }
};
